import { formatDue } from './time';
import { normalizeDraft } from './completeProject';
import { handoffTypeOf, listOf } from './taskList';

/**
 * 워크보드 행동 — 지금은 화면 상태(목 데이터)만 바꾼다.
 * 백엔드가 생기면 각 행동이 API 호출 + 이벤트 1건이 되고 (MVP §1 원칙 5),
 * 여기 reducer 는 응답으로 화면을 갱신하는 역할만 남는다. 알림도 서버가 만든다.
 */

const KST = 'Asia/Seoul';

const updateTask = (data, taskId, patch) => ({
  ...data,
  tasks: data.tasks.map((t) => (t.id === taskId ? { ...t, ...patch(t) } : t)),
});

const nameOf = (data, id) => data.members.find((m) => m.id === id)?.name ?? 'Someone';
const leaderId = (data) => data.members.find((m) => m.role === 'leader')?.id;

/** 예상 제출 시각이 바뀌면 알릴 사람 — 리더 · 받는 사람(기다리는 사람) · 다른 담당자 (바꾼 사람 빼고, 한 번씩) */
const expectedWatchers = (data, taskId) => {
  const task = data.tasks.find((t) => t.id === taskId);
  if (!task) return [];
  return [...new Set([leaderId(data), task.receiverId, ...assigneesOf(task)])].filter(
    (id) => id && id !== 'npc' && id !== data.viewerId,
  );
};
const titleOf = (data, taskId) => data.tasks.find((t) => t.id === taskId)?.title ?? 'a task';

/**
 * 알림 쌓기 (알림 목록 #23). 받는 사람이 행동한 본인이면 보내지 않는다.
 * 알림은 행동이 아니라서 이벤트 로그에는 남기지 않는다.
 */
// 알림 id — 한 행동에서 여러 번 알려도 겹치지 않게 순번을 붙인다
let noteSeq = 0;
const notify = (data, userIds, text, link, at, kind = null) => {
  const targets = [...new Set(userIds.filter((id) => id && id !== data.viewerId))];
  if (targets.length === 0) return data;
  const stamp = Date.now();
  return {
    ...data,
    notifications: [
      ...targets.map((userId) => ({
        id: `n-${stamp}-${(noteSeq += 1)}`,
        userId,
        text,
        link,
        createdAt: at,
        readAt: null,
        kind,
        actorId: data.viewerId,
      })),
      ...(data.notifications ?? []),
    ],
  };
};

const taskLink = (taskId, commentId) => ({
  to: 'task',
  id: taskId,
  ...(commentId && { commentId }),
});
/** 담당자 전원 (공동 담당자 포함) */
const assigneesOf = (t) =>
  t?.assigneeIds?.length ? t.assigneeIds : t?.assigneeId ? [t.assigneeId] : [];
const logged = (t, entry) => [...(t.activity ?? []), entry];

export const workboardReducer = (data, action) => {
  if (!data) return data;
  const me = nameOf(data, data.viewerId);
  switch (action.type) {
    /** 리더의 진행 중 배정 → assigned. 담당자에게 알림 */
    case 'addTask': {
      const next = { ...data, tasks: [...data.tasks, action.task] };
      return notify(
        next,
        assigneesOf(action.task),
        `${me} assigned you “${action.task.title}”`,
        taskLink(action.task.id),
        action.task.assignedAt,
        'task_assigned',
      );
    }

    /** PM 이 만든 태스크 고치기 — 제목 · 계획 시작 · 마감 · 과제 항목 · 스킬 태그. 담당자에게 알림 */
    case 'editTask': {
      const task = data.tasks.find((t) => t.id === action.taskId);
      if (!task) return data;
      const listChanged = JSON.stringify(task.taskList ?? []) !== JSON.stringify(action.task.taskList);
      const next = updateTask(data, action.taskId, () => ({
        title: action.task.title,
        plannedStartAt: action.task.startAt,
        dueAt: action.task.dueAt,
        taskList: action.task.taskList,
        taskSources: action.task.taskList.map(() => 'pm'),
        skillTags: action.task.skillTags,
        ...(listChanged ? { listChanged: true } : {}),
      }));
      return notify(
        next,
        assigneesOf(task),
        `${me} edited “${action.task.title}”`,
        taskLink(action.taskId),
        action.at,
        'task_edited',
      );
    }

    /** 개발용 — 보는 사람 바꾸기 (PM ↔ 팀원). 화면 상태는 그대로 둔다 */
    case 'setViewer':
      return { ...data, viewerId: action.viewerId };

    /** 담당자가 새 배정·바뀐 리스트를 처음 열어봄 — 카드의 빨간 점(안 읽음)·List changed 가 사라진다 */
    case 'openTask':
      return updateTask(data, action.taskId, (t) =>
        assigneesOf(t).includes(data.viewerId)
          ? { openedAt: t.openedAt ?? action.at, listChanged: false }
          : {},
      );

    /** 수락 → in_progress. 예상 제출 시각 필수 */
    case 'accept':
      return updateTask(data, action.taskId, () => ({
        state: 'in_progress',
        acceptedAt: action.at,
        acceptedById: data.viewerId,
        expectedSubmitAt: action.expectedSubmitAt,
      }));

    /** 거절 → declined. 사유 필수. 리더가 재배정해야 하니 리더에게 알림 */
    case 'decline': {
      const next = updateTask(data, action.taskId, (t) => ({
        state: 'declined',
        declineReason: action.reason,
        activity: logged(t, { type: 'decline', at: action.at, by: data.viewerId, reason: action.reason }),
      }));
      return notify(
        next,
        [leaderId(data)],
        `${me} declined “${titleOf(data, action.taskId)}”`,
        taskLink(action.taskId),
        action.at,
        'task_declined',
      );
    }

    /** 결과물 제출 = 인계 제출 → submitted. 받는 사람에게 알림 */
    case 'submit': {
      const task = data.tasks.find((t) => t.id === action.taskId);
      // 이전 버전은 history 에 쌓는다 (버전 카드)
      const next = updateTask(data, action.taskId, (t) => ({
        state: 'submitted',
        isBlocked: false,
        history: t.deliverable
          ? [
              ...(t.history ?? []),
              {
                version: (t.history?.length ?? 0) + 1,
                deliverable: t.deliverable,
                review: t.review,
              },
            ]
          : (t.history ?? []),
        version: t.deliverable ? (t.version ?? 1) + 1 : (t.version ?? 1),
        deliverable: {
          link: action.link ?? action.links?.[0] ?? null,
          links: action.links ?? (action.link ? [action.link] : []),
          linkKinds: action.linkKinds ?? [],
          files: action.files ?? [],
          // 인계 유형별 링크 슬롯 [{ k, l, url }] · 최종 결과물 한 장 요약 [{ q, a }]
          ...(action.slots ? { slots: action.slots } : {}),
          ...(action.onePage ? { onePage: action.onePage } : {}),
          // 개발 인계 링크 확인 결과 — 서버는 제출할 때 직접 다시 확인해서 남긴다 (이 값은 목 보드용)
          ...(action.checks ? { checks: action.checks } : {}),
          summary: action.summary,
          submittedAt: action.at,
          submittedBy: data.viewerId,
          checked: action.items ?? [],
        },
        review: null,
      }));
      // 팀장(NPC)이 받으면 In review 없이 바로 Done — 운영진은 Lead inbox 에서 보고 필요하면 되돌린다
      const receiver = data.members.find((m) => m.id === task?.receiverId);
      if (task?.receiverId === 'npc' || receiver?.role === 'npc') {
        return updateTask(next, action.taskId, () => ({
          state: 'approved',
          approvedSubmittedAt: action.at,
          review: { result: 'approved', at: action.at, auto: true },
        }));
      }
      return notify(
        next,
        [task?.receiverId],
        `${me} handed off “${task?.title}” for your review`,
        taskLink(action.taskId),
        action.at,
        'handoff_submitted',
      );
    }

    /** 승인 — Task List 전부 ✓. 승인된 버전의 제출 시각이 기한 준수 기준이 된다 (MVP §8-2) */
    case 'approve': {
      const task = data.tasks.find((t) => t.id === action.taskId);
      const next = updateTask(data, action.taskId, (t) => ({
        state: 'approved',
        approvedSubmittedAt: t.deliverable?.submittedAt ?? action.at,
        review: { result: 'approved', at: action.at },
      }));
      let out = notify(
        next,
        assigneesOf(task),
        `${me} approved “${task?.title}”`,
        taskLink(action.taskId),
        action.at,
        'handoff_approved',
      );
      // 앞 단계가 모두 승인된 다음 태스크 담당자에게 — 이제 제출할 수 있다
      next.tasks
        .filter((t) => (t.predecessorIds ?? []).includes(action.taskId))
        .filter((t) =>
          t.predecessorIds.every((id) =>
            ['approved', 'cancelled'].includes(next.tasks.find((x) => x.id === id)?.state),
          ),
        )
        .forEach((t) => {
          out = notify(
            out,
            assigneesOf(t),
            `“${task?.title}” was approved. You can now submit “${t.title}”`,
            taskLink(t.id),
            action.at,
            'submit_unlocked',
          );
        });
      return out;
    }

    /** 반려 — ✗ 항목 + 사유 + 설명 → rework_requested */
    case 'reject': {
      const task = data.tasks.find((t) => t.id === action.taskId);
      const next = {
        ...updateTask(data, action.taskId, () => ({
          state: 'rework_requested',
          review: {
            result: 'rejected',
            failedItems: action.failedItems,
            passedItems: (action.items ?? []).filter((x) => !action.failedItems.includes(x)),
            reason: action.reason,
            description: action.description,
            at: action.at,
          },
        })),
        // 반려 사유는 댓글로도 남는다
        comments: [
          ...(data.comments ?? []),
          {
            id: `c-${Date.now()}`,
            taskId: action.taskId,
            authorId: data.viewerId,
            type: 'rejection',
            targetUserId: task?.assigneeId,
            targetUserIds: assigneesOf(task),
            body: action.description,
            reason: action.reason,
            detail: `Marked ✗: ${action.failedItems
              .map((x) =>
                (task?.deliverable?.slots ?? []).some((s) => s.l === x) ? `Link slot · ${x}` : x,
              )
              .join(', ')}`,
            createdAt: action.at,
            parentId: null,
          },
        ],
      };
      return notify(
        next,
        assigneesOf(task),
        `${me} asked for changes on “${task?.title}”`,
        taskLink(action.taskId),
        action.at,
        'handoff_rejected',
      );
    }

    /** 막힘 해소 — 도와준 사람은 기여자로 함께 기록 */
    case 'resolveBlocker':
      return updateTask(data, action.taskId, (t) => ({
        isBlocked: false,
        contributors: action.helpedBy
          ? [
              ...t.contributors,
              {
                userId: action.helpedBy,
                reason: 'Blocker resolved',
                addedBy: data.viewerId,
                at: action.at,
              },
            ]
          : t.contributors,
        ...(action.helpedBy
          ? {}
          : { activity: logged(t, { type: 'blocker', at: action.at, by: data.viewerId }) }),
      }));

    /** 예상 제출 시각 변경 — 늦추기만. 바꿀 때마다 리더에게 알림 */
    case 'changeExpected': {
      const next = updateTask(data, action.taskId, (t) => ({
        expectedSubmitAt: action.value,
        activity: logged(t, {
          type: 'expected',
          at: action.at,
          by: data.viewerId,
          from: t.expectedSubmitAt ?? null,
          to: action.value,
        }),
      }));
      return notify(
        next,
        expectedWatchers(data, action.taskId),
        `${me} moved the expected submit time for “${titleOf(data, action.taskId)}”: ${formatDue(action.value, KST)} KST`,
        taskLink(action.taskId),
        action.at,
        'expected_changed',
      );
    }

    /** 리스트가 바뀐 뒤 예상 제출 시각 다시 정하기 — 1회 */
    case 'resetExpected': {
      const next = updateTask(data, action.taskId, (t) => ({
        expectedSubmitAt: action.value,
        expectedResetLeft: false,
        activity: logged(t, {
          type: 'expected',
          at: action.at,
          by: data.viewerId,
          from: t.expectedSubmitAt ?? null,
          to: action.value,
        }),
      }));
      return notify(
        next,
        expectedWatchers(data, action.taskId),
        `${me} reset the expected submit time for “${titleOf(data, action.taskId)}”: ${formatDue(action.value, KST)} KST`,
        taskLink(action.taskId),
        action.at,
        'expected_changed',
      );
    }

    /** 리더 받는 사람 변경 — 새 받는 사람과 담당자에게 알림 */
    /** 태스크 지우기 (PM 이 만든 것 · 거절된 것) — 한 번도 제출되지 않았을 때. 댓글도 지우고 뒤 태스크의 앞 단계에서 뺀다. 사유는 알림에 */
    case 'deleteTask': {
      const task = data.tasks.find((t) => t.id === action.taskId);
      if (!task) return data;
      const next = {
        ...data,
        tasks: data.tasks
          .filter((t) => t.id !== action.taskId)
          .map((t) =>
            (t.predecessorIds ?? []).includes(action.taskId)
              ? { ...t, predecessorIds: t.predecessorIds.filter((x) => x !== action.taskId) }
              : t,
          ),
        comments: (data.comments ?? []).filter((c) => c.taskId !== action.taskId),
      };
      return notify(
        next,
        assigneesOf(task),
        `${me} deleted “${task.title}”${action.reason ? `: ${action.reason}` : ''}`,
        { to: 'tasks' },
        action.at,
        'task_cancelled',
      );
    }

    case 'changeReceiver': {
      const task = data.tasks.find((t) => t.id === action.taskId);
      const next = updateTask(data, action.taskId, (t) => ({
        receiverId: action.userId,
        activity: logged(t, {
          type: 'receiver',
          at: action.at,
          by: 'leader',
          from: t.receiverId,
          to: action.userId,
          reason: action.reason,
        }),
      }));
      const name = nameOf(data, action.userId);
      const waiting = task?.state === 'submitted';
      return notify(
        notify(
          next,
          [action.userId],
          `${me} made you the receiver of “${task?.title}”${waiting ? '. It’s waiting for your review' : ''}`,
          taskLink(action.taskId),
          action.at,
          waiting ? 'review_assigned' : 'receiver_changed',
        ),
        assigneesOf(task),
        `${me} changed the receiver of “${task?.title}” to ${name}`,
        taskLink(action.taskId),
        action.at,
        'receiver_changed',
      );
    }

    /** 기여자 추가 — 도움 받은 사람이 추가한다 */
    case 'addContributor':
      return updateTask(data, action.taskId, (t) => ({
        contributors: [
          ...t.contributors,
          {
            userId: action.userId,
            reason: action.reason,
            commentId: action.commentId || null,
            addedBy: data.viewerId,
            at: action.at,
          },
        ],
      }));

    /** 댓글 — 수정·삭제 없음. 막힘 요청이면 태스크가 Blocked 가 된다 */
    case 'addComment': {
      const withComment = { ...data, comments: [...(data.comments ?? []), action.comment] };
      const next =
        action.comment.type === 'blocker'
          ? updateTask(withComment, action.comment.taskId, () => ({ isBlocked: true }))
          : withComment;
      // 내가 맡은 태스크에 댓글이 달리면 담당자에게 (질문·도움 요청 받은 사람은 그 알림으로 충분)
      const task = data.tasks.find((t) => t.id === action.comment.taskId);
      const told = new Set([
        action.comment.targetUserId,
        ...(action.comment.targetUserIds ?? []),
        (data.comments ?? []).find((c) => c.id === action.comment.parentId)?.authorId,
      ]);
      const link = taskLink(action.comment.taskId, action.comment.id);
      const at = action.comment.createdAt;
      const blocker = action.comment.type === 'blocker';
      const asked = ['question', 'blocker'].includes(action.comment.type)
        ? [action.comment.targetUserId, ...(action.comment.targetUserIds ?? [])].filter(
            (id) => id && id !== 'npc',
          )
        : [];
      const parentAuthor = (data.comments ?? []).find(
        (c) => c.id === action.comment.parentId,
      )?.authorId;
      let out = notify(
        next,
        asked,
        `${me} asked you ${blocker ? 'for help' : 'a question'} on “${task?.title}”`,
        link,
        at,
        blocker ? 'blocker_raised' : 'question_asked',
      );
      if (parentAuthor && parentAuthor !== 'npc') {
        out = notify(
          out,
          [parentAuthor],
          `${me} replied to your comment on “${task?.title}”`,
          link,
          at,
          'comment_replied',
        );
      }
      // @멘션된 사람 — 담당자와 달라도 알린다 (이미 알림을 받는 사람은 빼고)
      const mentioned = (action.comment.mentionIds ?? []).filter((id) => !told.has(id));
      mentioned.forEach((id) => told.add(id));
      const withMentions = notify(
        out,
        mentioned,
        `${me} mentioned you on “${task?.title}”`,
        link,
        at,
        'comment_mentioned',
      );
      return notify(
        withMentions,
        assigneesOf(task).filter((id) => !told.has(id)),
        `${me} commented on your task “${task?.title}”`,
        link,
        at,
        'comment_added',
      );
    }

    /** 이모지 반응 — 누르면 켜고 다시 누르면 끈다. 켜면 댓글 쓴 사람의 Inbox Activity 에 */
    /** 내 댓글 고치기 — 이전 본문은 서버에 남는다. (edited) 표시 */
    case 'editComment':
      return {
        ...data,
        comments: (data.comments ?? []).map((c) =>
          c.id === action.commentId
            ? {
                ...c,
                body: action.comment.body,
                mentionIds: action.comment.mentionIds ?? [],
                editedAt: action.at,
              }
            : c,
        ),
      };

    /** 내 댓글 지우기 — 자리는 남고 본문 · 사진 · 반응은 비운다 (답글은 그대로) */
    case 'deleteComment':
      return {
        ...data,
        comments: (data.comments ?? []).map((c) =>
          c.id === action.commentId
            ? { ...c, body: '', files: [], reactions: {}, mentionIds: [], deletedAt: action.at }
            : c,
        ),
      };

    case 'reactComment': {
      const target = (data.comments ?? []).find((c) => c.id === action.commentId);
      if (!target) return data;
      const added = !(target.reactions?.[action.emoji] ?? []).includes(data.viewerId);
      const next = {
        ...data,
        comments: (data.comments ?? []).map((c) => {
          if (c.id !== action.commentId) return c;
          const who = c.reactions?.[action.emoji] ?? [];
          const nextWho = added
            ? [...who, data.viewerId]
            : who.filter((id) => id !== data.viewerId);
          const reactions = { ...(c.reactions ?? {}), [action.emoji]: nextWho };
          if (!nextWho.length) delete reactions[action.emoji];
          return { ...c, reactions };
        }),
      };
      return added && target.authorId !== 'npc'
        ? notify(
            next,
            [target.authorId],
            `${me} reacted ${action.emoji} to your comment`,
            taskLink(target.taskId, target.id),
            action.at ?? new Date().toISOString(),
            'comment_reacted',
          )
        : next;
    }

    /** 질문 해결 — 질문을 단 사람만 */
    case 'resolveComment':
      return {
        ...data,
        comments: (data.comments ?? []).map((c) =>
          c.id === action.commentId ? { ...c, resolvedAt: action.at } : c,
        ),
      };

    /** 받는 사람의 되돌림 — 승인 뒤 열린 질문을 근거로 → rework_requested */
    case 'returnHandoff': {
      const task = data.tasks.find((t) => t.id === action.taskId);
      const q = (data.comments ?? []).find((c) => c.id === action.commentId);
      const next = {
        ...updateTask(data, action.taskId, () => ({
          state: 'rework_requested',
          approvedSubmittedAt: null,
          review: {
            result: 'rejected',
            returned: true,
            failedItems: [],
            reason: action.reason,
            description: action.description,
            commentId: action.commentId,
            at: action.at,
          },
        })),
        comments: [
          ...(data.comments ?? []),
          {
            id: `c-${Date.now()}`,
            taskId: action.taskId,
            authorId: data.viewerId,
            type: 'return',
            targetUserId: task?.assigneeId,
            body: action.description,
            reason: action.reason,
            detail: q ? `Linked question: ${me} · ${formatDue(q.createdAt, KST)}` : null,
            createdAt: action.at,
            parentId: null,
          },
        ],
      };
      return notify(
        next,
        assigneesOf(task),
        `${me} returned “${task?.title}”`,
        taskLink(action.taskId, action.commentId),
        action.at,
        'handoff_returned',
      );
    }

    /** 리더 재배정 — 새 담당자에게 처음부터 */
    case 'reassign': {
      const task = data.tasks.find((t) => t.id === action.taskId);
      const memberById = new Map(data.members.map((m) => [m.id, m]));
      const next = updateTask(data, action.taskId, (t) => ({
        // 인계 유형은 그대로 — 다른 직무에게 넘겨도 Task List 기본 항목 · 제출 칸이 남는다 (서버와 같다)
        handoffType: t.handoffType !== undefined ? t.handoffType : handoffTypeOf(t, memberById),
        assigneeId: action.userId,
        assigneeIds: [action.userId],
        state: 'assigned',
        assignedAt: action.at,
        openedAt: null,
        acceptedAt: null,
        acceptedById: null,
        expectedSubmitAt: null,
        expectedResetLeft: false,
        listChanged: false,
        declineReason: null,
        isBlocked: false,
        activity: [
          ...(t.activity ?? []).filter((e) => e.type !== 'expected'),
          {
            type: 'reassign',
            at: action.at,
            by: 'leader',
            from: t.assigneeId,
            to: action.userId,
            reason: action.reason,
          },
        ],
      }));
      return notify(
        notify(
          next,
          [action.userId],
          `${me} assigned you “${task?.title}”`,
          taskLink(action.taskId),
          action.at,
          'task_assigned',
        ),
        assigneesOf(task).filter((id) => id !== action.userId),
        `${me} reassigned “${task?.title}” to ${nameOf(data, action.userId)}`,
        taskLink(action.taskId),
        action.at,
        'task_reassigned',
      );
    }

    /** 리더 범위 변경 — 과제 항목을 바꾼다. 담당자 전원에게 알림 */
    case 'changeScope': {
      const task = data.tasks.find((t) => t.id === action.taskId);
      const { items, sources } = listOf(task, new Map(data.members.map((m) => [m.id, m])));
      const teamStart = data.project.teamStartAt
        ? new Date(data.project.teamStartAt)
        : new Date(new Date(data.project.startAt).getTime() + 3_600_000);
      const afterStart = new Date(action.at) >= teamStart;
      const next = updateTask(data, action.taskId, (t) => ({
        activity: logged(t, { type: 'scope', at: action.at, by: 'leader', reason: action.reason }),
        taskList: action.items,
        taskSources: action.items.map((text, i) => {
          const o = action.origins?.[i];
          if (o == null) return 'pm';
          const src = sources?.[o] ?? (t.code ? 'ops' : 'pm');
          return text !== items[o] && src === 'ops' ? 'pm_edit' : src;
        }),
        ...(afterStart
          ? {
              listChanged: true,
              expectedResetLeft:
                t.acceptedAt && t.state !== 'assigned' ? true : t.expectedResetLeft,
            }
          : {}),
      }));
      return notify(
        next,
        assigneesOf(task),
        `${me} changed the Task List of “${task?.title}” · ${action.reason}`,
        taskLink(action.taskId),
        action.at,
        'scope_changed',
      );
    }

    /**
     * 체크인 (수정도 같은 행동). 태스크별 예상 제출 시각도 함께 바꾼다 — 늦추기만.
     * 체크인과 예상 시각 변경은 리더에게 알린다.
     */
    case 'checkIn': {
      const cp = data.checkpoints.find((c) => c.id === action.checkpointId);
      const others = (data.checkins ?? []).filter(
        (c) => !(c.checkpointId === action.checkpointId && c.userId === data.viewerId),
      );
      const prev = (data.checkins ?? []).find(
        (c) => c.checkpointId === action.checkpointId && c.userId === data.viewerId,
      );
      let next = {
        ...data,
        checkins: [
          ...others,
          {
            checkpointId: action.checkpointId,
            userId: data.viewerId,
            // 처음 체크인한 시각으로 출석을 판정한다 — 나중에 내용을 고쳐도 지각이 되지 않는다
            checkedInAt: prev?.checkedInAt ?? action.at,
            note: action.note,
            updatedAt: action.at,
          },
        ],
      };
      const moved = action.expected.filter((e) => {
        const t = data.tasks.find((x) => x.id === e.taskId);
        return t && (!t.expectedSubmitAt || new Date(e.value) > new Date(t.expectedSubmitAt));
      });
      moved.forEach((e) => {
        next = updateTask(next, e.taskId, () => ({ expectedSubmitAt: e.value }));
      });
      const extra = moved.length
        ? ` and moved ${moved.length} expected submit time${moved.length > 1 ? 's' : ''}`
        : '';
      return notify(
        next,
        [leaderId(data)],
        `${me} ${prev ? 'updated their check-in for' : 'checked in to'} ${cp?.name}${extra}`,
        { to: 'checkpoints' },
        action.at,
        'checked_in',
      );
    }

    /** general 체크포인트 만들기 — 리더만. 대상 직군에게 알림 */
    case 'addCheckpoint': {
      const next = { ...data, checkpoints: [...data.checkpoints, action.checkpoint] };
      const targets = data.members
        .filter((m) => m.status === 'active' && action.checkpoint.targetJobs.includes(m.job))
        .map((m) => m.id);
      return notify(
        next,
        targets,
        `${me} added a checkpoint “${action.checkpoint.name}” at ${formatDue(action.checkpoint.scheduledAt, KST)} KST`,
        { to: 'checkpoints' },
        action.at,
        'checkpoint_created',
      );
    }

    /** general 체크포인트 수정 (이름·시간·대상 직군) — 저장하면 바뀌기 전·후 대상자 모두에게 알림 */
    case 'editCheckpoint': {
      const cp = data.checkpoints.find((c) => c.id === action.checkpointId);
      const patch = action.patch;
      const next = {
        ...data,
        checkpoints: data.checkpoints.map((c) =>
          c.id === action.checkpointId ? { ...c, ...patch } : c,
        ),
      };
      const jobs = new Set([...(cp?.targetJobs ?? []), ...(patch.targetJobs ?? [])]);
      const targets = data.members
        .filter((m) => m.status === 'active' && jobs.has(m.job))
        .map((m) => m.id);
      return notify(
        next,
        targets,
        `${me} updated “${patch.name ?? cp?.name}”: ${formatDue(patch.scheduledAt ?? cp?.scheduledAt, KST)} KST`,
        { to: 'checkpoints' },
        action.at,
        'checkpoint_updated',
      );
    }

    /** general 체크포인트 삭제 — 체크인이 0건일 때만 */
    case 'deleteCheckpoint':
      if ((data.checkins ?? []).some((c) => c.checkpointId === action.checkpointId)) return data;
      return { ...data, checkpoints: data.checkpoints.filter((c) => c.id !== action.checkpointId) };

    /**
     * 타임라인 조정 (팀원 누구나). 막대를 옮기거나 폭을 바꾼 결과를 한 번에 저장한다.
     * 태스크의 계획 시작·마감이 바뀌고, 바뀐 태스크의 담당자에게 알림이 간다.
     */
    case 'adjustSchedule': {
      let next = data;
      // 마감이 바뀌면 수락한 담당자는 예상 제출 시각을 1회 다시 정할 수 있다 · 뒤 태스크 마감은 그대로
      action.changes.forEach((c) => {
        next = updateTask(next, c.taskId, (t) => ({
          plannedStartAt: c.startAt,
          dueAt: c.dueAt,
          ...(t.acceptedAt &&
            t.state !== 'assigned' &&
            new Date(c.dueAt).getTime() !== new Date(t.dueAt).getTime() && {
              expectedResetLeft: true,
            }),
        }));
      });
      action.changes.forEach((c) => {
        const t = data.tasks.find((x) => x.id === c.taskId);
        next = notify(
          next,
          assigneesOf(t),
          `${me} changed the schedule for “${t?.title}”: due ${formatDue(c.dueAt, KST)} KST`,
          taskLink(c.taskId),
          action.at,
          'timeline_adjusted',
        );
      });
      return next;
    }

    /** 내 프로필 — 작업 시간(시간대·구간 여러 개)과 툴·스택. 알림 없음 */
    case 'updateProfile':
      return {
        ...data,
        members: data.members.map((m) =>
          m.id === data.viewerId
            ? {
                ...m,
                timezone: action.timezone,
                availableFrom: action.availableFrom,
                availableTo: action.availableTo,
                availableSlots: action.availableSlots,
                tools: action.tools,
                stack: action.stack,
              }
            : m,
        ),
      };

    /** 프로필 사진 — 256px data URL, null 이면 지운다 (본인만 보인다) */
    /** 닉네임 바꾸기 — 실명 표시에 동의하지 않은(anon) 팀원만. 보드에 보이는 이름도 바뀐다 */
    case 'updateNickname':
      if (data.me?.nameDisplay !== 'anon') return data;
      return {
        ...data,
        me: { ...data.me, nickname: action.nickname },
        members: data.members.map((m) =>
          m.id === data.viewerId ? { ...m, name: action.nickname } : m,
        ),
      };
    case 'updatePhoto':
      return { ...data, me: { ...(data.me ?? {}), photo: action.photo || null } };

    /** 목 보드 전용 — Complete project 초안 저장·제출 */
    case 'cpSaveDraft':
    case 'cpSubmit': {
      const cp = data.completeProject;
      if (!cp) return data;
      const mine = cp.byMember?.[data.viewerId] ?? {};
      const patch =
        action.type === 'cpSaveDraft'
          ? { draft: action.update(normalizeDraft(mine.draft)), savedAt: action.at }
          : { submittedAt: action.at, late: !!action.late };
      return {
        ...data,
        completeProject: {
          ...cp,
          byMember: { ...cp.byMember, [data.viewerId]: { ...mine, ...patch } },
        },
      };
    }

    /** 알림 읽음 — 하나(notificationId) 또는 여러 개(notificationIds, 반응 묶음) */
    case 'readNotification': {
      const ids = new Set(action.notificationIds ?? [action.notificationId]);
      return {
        ...data,
        notifications: (data.notifications ?? []).map((n) =>
          ids.has(n.id) && !n.readAt ? { ...n, readAt: action.at } : n,
        ),
      };
    }

    /** 내 알림 모두 읽음 */
    case 'markAllRead':
      return {
        ...data,
        notifications: (data.notifications ?? []).map((n) =>
          n.userId === data.viewerId && !n.readAt ? { ...n, readAt: action.at } : n,
        ),
      };

    default:
      return data;
  }
};

/**
 * 받는 사람 기본값 (MVP §6-2, A안) — 직군 기준.
 *   기획 → 디자이너 · 디자인 → 개발자 · 개발 → 기획자(QA) · 마케터 → 기획자
 *   해당 직군의 진행 중 팀원이 없으면 팀장.
 */
const NEXT_JOB = {
  planner: 'designer',
  designer: 'developer',
  developer: 'planner',
  marketer: 'planner',
};

export const defaultReceiverFor = (assignee, members) => {
  if (!assignee) return null;
  const job = NEXT_JOB[assignee.job];
  const next = members.find((m) => m.job === job && m.status === 'active' && m.id !== assignee.id);
  return next ?? members.find((m) => m.role === 'npc') ?? null;
};
